import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  GridDisplay,
  Heading2,
  Space,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CWC_WHATS_NEW } from '../../config'
import { LandingPageContentSections, TrackingActions, trackCTAClick } from '../../tracking'
import whatsnewBoxWebp from '../../assets/WhatsNewSectionAssets/whatsnew-box.webp'
import whatsnewBoxJpg from '../../assets/WhatsNewSectionAssets/whatsnew-box.jpg'
import whatsnewFastWebp from '../../assets/WhatsNewSectionAssets/whatsnew-fast.webp'
import whatsnewFastJpg from '../../assets/WhatsNewSectionAssets/whatsnew-fast.jpg'
import whatsnewHeatEatWebp from '../../assets/WhatsNewSectionAssets/whatsnew-heateat.webp'
import whatsnewHeatEatJpg from '../../assets/WhatsNewSectionAssets/whatsnew-heateat.jpg'

import { CWCAppearance } from '../CWCAppearance/CWCAppearance'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { CWCFeatureCard, CWCFeatureCardProps } from './components/CWCFeatureCard'
import { CWCFloatingPills } from '../CWCFloatingPills/CWCFloatingPills'
import { CWCNewBadge } from '../CWCNewBadge/CWCNewBadge'

// Aligned by index to CWC_WHATS_NEW.cards. Each card serves WebP with a JPEG fallback.
const CARD_IMAGES = [
  { webp: whatsnewFastWebp, jpg: whatsnewFastJpg },
  { webp: whatsnewHeatEatWebp, jpg: whatsnewHeatEatJpg },
  { webp: whatsnewBoxWebp, jpg: whatsnewBoxJpg },
]

// Per-card cover-image framing (aligned by index to CWC_WHATS_NEW.cards).
//
// Card 1 ("Super tasty, super fast") uses a tall portrait image showing the full plate; object-fit
// cover would otherwise centre on the falling leaves, so `imagePosition` anchors the crop low to keep
// the plate in frame at every breakpoint (no zoom needed — the asset already shows the whole plate).
//
// Card 3 ("Built for real life") uses a landscape image whose box sits centre-frame with the door
// above it; it shares the column aspect so cover does no vertical crop. Scaling about the TOP edge
// (`imageZoomOrigin`) drops the box downward as it enlarges, so it clears the floating pills on mobile
// only (tablet/desktop keep it unzoomed, where the box is already clear).
//
// `undefined` = natural cover framing. Values verified via the figma-fidelity-loop across breakpoints.
const CARD_IMAGE_POSITIONS: CWCFeatureCardProps['imagePosition'][] = [
  'center 82%', // card 1 — frame the plate at the bottom of the portrait
  undefined, // card 2 — centre is correct
  undefined, // card 3 — centre is correct (framed via zoom below)
]
const CARD_IMAGE_ZOOMS: CWCFeatureCardProps['imageZoom'][] = [
  undefined, // card 1 — no zoom (portrait asset already shows the whole plate)
  undefined, // card 2 — no zoom needed
  [1.25, null, 1], // card 3 — drop the box below the pills on mobile only
]
// Only card 3 zooms; scaling about the top edge moves its box down, clear of the pills.
const CARD_IMAGE_ZOOM_ORIGINS: (string | undefined)[] = [undefined, undefined, 'center top']

// Dark section background: Citrus `ColdGrey_800` (#333D47). Vertical padding matches the platform
// standard used across the CwC homepage: 56px mobile/tablet, 80px desktop.
const SectionContainer = withCitrusTheme(Box, (theme) => [
  { backgroundColor: theme.color(Color.ColdGrey_800) },
  theme.responsiveStyle(['56px', null, null, '80px'], ['paddingTop', 'paddingBottom']),
])

// "What's new" heading: Axiforma ExtraBold 36px / 32px line-height (exact Figma).
// Uses responsiveStyle (media-query specificity) so it overrides Heading2's own
// responsive font-size at every breakpoint.
const SectionHeading = withCitrusTheme(Heading2, (theme) => [
  theme.responsiveStyle(['36px', null, '36px'], ['fontSize']),
  theme.responsiveStyle(['32px', null, '32px'], ['lineHeight']),
])

// Equal-height cards: a single-column grid with `grid-auto-rows: 1fr` sizes every row to the tallest
// card's content, so the cards stay uniform while growing to fit their copy (no overflow at narrow
// widths like iPhone SE). `gridAutoRows` isn't a Box prop, so it's set here.
const CardsGrid = withCitrusTheme(Box, () => ({
  gridAutoRows: '1fr',
}))

export type CWCWhatsNewSectionProps = {
  /** Sign-up destination the CTA links to (kept as the anchor `href` for semantics/accessibility). */
  signupUrl: string
  /**
   * Shared signup handler (applies the promo code, then redirects). Because this CTA leads to
   * sign-up, the click prevents the raw anchor navigation and dispatches this instead, so the promo
   * is applied — matching the hero CTA. CTAs to non-signup pages would omit this and navigate via
   * `href`.
   */
  onCTAClick: () => void
}

export const CWCWhatsNewSection = ({ signupUrl, onCTAClick }: CWCWhatsNewSectionProps) => {
  const handleSignupClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, LandingPageContentSections.WHATS_NEW)
    onCTAClick()
  }

  return (
    <section data-testid="whatsNewSection">
      <SectionContainer
        paddingH={[4, null, 6]}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
      >
        {/* The heading reveals as the visitor reaches the section; the cards then reveal
            individually as each crosses in (below), so the whole section animates in coherently
            rather than the heading appearing statically above animating cards. */}
        <CWCAppearance>
          <SectionHeading
            color={Color.White}
            fontFamily={FontFamily.ExtraBold}
            textAlign={TextAlign.Center}
          >
            {CWC_WHATS_NEW.heading}
          </SectionHeading>
        </CWCAppearance>

        <Space size={8} />

        <CardsGrid
          maxWidth="640px"
          width="100%"
          display={GridDisplay.Grid}
          gridTemplateColumns="1fr"
          gap={6}
        >
          {/* Each card animates in independently as it crosses into the viewport (its own reveal),
              so the cards reveal progressively as the visitor scrolls — not the whole grid at once.
              The badge is inside the card, so it reveals with its card. */}
          {CWC_WHATS_NEW.cards.map((card, index) => (
            <CWCAppearance key={card.title}>
              <CWCFeatureCard
                title={card.title}
                body={card.body}
                image={CARD_IMAGES[index]}
                imageAlt={card.imageAlt}
                imageZoom={CARD_IMAGE_ZOOMS[index]}
                imageZoomOrigin={CARD_IMAGE_ZOOM_ORIGINS[index]}
                imagePosition={CARD_IMAGE_POSITIONS[index]}
                // The "Built for real life" (box) card carries the decorative floating pills.
                imageOverlay={
                  card.title === 'Built for real life' ? <CWCFloatingPills /> : undefined
                }
                badge={
                  <CWCNewBadge width={['51px', null, '78px']} height={['36px', null, '56px']} />
                }
              />
            </CWCAppearance>
          ))}
        </CardsGrid>

        <Space size={10} />

        <Box width={['100%', '345px', '345px', '345px']}>
          <CWCAppearance>
            <CWCLinkButton href={signupUrl} onClick={handleSignupClick}>
              {CWC_WHATS_NEW.cta}
            </CWCLinkButton>
          </CWCAppearance>
        </Box>
      </SectionContainer>
    </section>
  )
}
